import {TFunction} from 'react-i18next';

import {showCropControlModal} from '../CropControl';
import {showNewNotification} from '../NewNotifications';
import {
  EImagePickerError,
  TCustomValidator,
  THandleUploadResult,
  TImageDimensions,
  TImageInfo,
  TImagePickerResult,
  TRestrictions,
} from './definitions';

const isValidUrl = (value: string) => /^((http|https|ftp):\/\/)/.test(value);

const requestFile = async (url: string): Promise<{file?: File; error?: TAnyObject}> => {
  try {
    const response = await fetch(url);
    const type = response.headers.get('Content-Type') ?? 'image/jpeg';

    if (response.ok) {
      const blob = await response.blob();

      return {
        file: new File([blob], url.split('/').pop() ?? 'filename', {
          type: type === 'image/jpg' ? 'image/jpeg' : type,
        }),
      };
    }

    const error = await response.json();

    return {error};
  } catch (e) {
    return {};
  }
};

const formatImageSize = (size?: number) => {
  if (!size) {
    return '';
  }

  const sizeKB = Math.round(size / 1024);
  const sizeMB = Math.round(sizeKB / 1024);

  if (sizeMB < 1) {
    return `${sizeKB} KB`;
  }

  return `${sizeMB} MB`;
};

const getFileDimensions = (file: File): Promise<TImageDimensions | undefined> =>
  new Promise((resolve, reject) => {
    try {
      const img = new Image();

      img.onload = () => {
        const width = img.naturalWidth,
          height = img.naturalHeight;

        window.URL.revokeObjectURL(img.src);

        if (width && height) {
          return resolve({width, height});
        }

        return resolve(undefined);
      };

      img.onerror = () => resolve(undefined);

      img.src = window.URL.createObjectURL(file);
    } catch (exception) {
      return reject(exception);
    }
  });

const checkImageFormat = (file: File, restrictions: TRestrictions): EImagePickerError | void => {
  const error = restrictions.accept ? EImagePickerError.ACCEPT : EImagePickerError.FORMAT;

  if (!(file instanceof File)) {
    return error;
  }

  if (!file.type.startsWith('image/')) {
    return error;
  }

  if (restrictions.accept) {
    const formats = restrictions.accept
      .toLowerCase()
      .replaceAll(/ +/g, ' ')
      .replaceAll(/[,.]/g, '')
      .split(' ');

    const mimeType = file.type.toLowerCase();
    const format = file.name.toLowerCase().split('.').pop() ?? '';

    if (!formats.includes(mimeType) && !formats.includes(format)) {
      return error;
    }
  }
};

const checkImageSize = (file: File, restrictions: TRestrictions): EImagePickerError | void => {
  const error = restrictions.sizeInBytes ? EImagePickerError.SIZE : EImagePickerError.FORMAT;

  if (!file.size || (restrictions.sizeInBytes && file.size > restrictions.sizeInBytes)) {
    return error;
  }
};

const checkImageDimensions = async (
  file: File,
  restrictions: TRestrictions,
): Promise<{dimensions?: TImageDimensions; error?: EImagePickerError}> => {
  const error =
    restrictions.width || restrictions.height
      ? EImagePickerError.DIMENSIONS
      : EImagePickerError.FORMAT;

  const dimensions = await getFileDimensions(file);

  if (!dimensions) {
    return {error};
  }

  if (!restrictions.width || !restrictions.height) {
    return {dimensions};
  }

  const isValidDimensions = restrictions.isExactDimensions
    ? restrictions.width === dimensions.width && restrictions.height === dimensions.height
    : dimensions.width >= restrictions.width && dimensions.height >= restrictions.height;

  if (!isValidDimensions) {
    return {error};
  }

  return {dimensions};
};

const getImageInfo = async (value: string): Promise<TImageInfo> => {
  const format = value.split('.').pop() ?? '';

  const dimensions: Partial<TImageDimensions> = {};
  const result = await requestFile(value);

  if (result?.file instanceof File) {
    const resultDimensions = await getFileDimensions(result.file);

    dimensions.width = resultDimensions?.width;
    dimensions.height = resultDimensions?.height;
  }

  return {
    format,
    width: dimensions.width,
    height: dimensions.height,
    url: value,
    name: result.file?.name ?? '',
    size: result.file?.size ?? 0,
  };
};

const handleCropFile = async (
  file: File,
  restrictions: TRestrictions,
): Promise<File | undefined> => {
  const result: File | undefined = await new Promise((resolve) =>
    showCropControlModal({
      file,
      minHeight: restrictions.height,
      minWidth: restrictions.width,
      aspectRatio: restrictions.aspectRatio,
      isExactParams: restrictions.isExactDimensions,
      onSubmit: (croppedFile?: File) => resolve(croppedFile),
    }),
  );

  return result;
};

const handleUpload = async ({
  files,
  restrictions,
  customValidator,
  hasCropper,
}: {
  files: File[];
  restrictions: TRestrictions;
  hasCropper?: boolean;
  customValidator?: TCustomValidator;
}): Promise<THandleUploadResult> => {
  if (!files.length) {
    return {successUploads: [], errorUploads: []};
  }

  const resolvedPromises = await Promise.allSettled(
    files.map(async (file) => {
      const formatError = checkImageFormat(file, restrictions);

      if (formatError) {
        return Promise.reject(formatError);
      }

      let resultFile: File | undefined | null = file;

      if (hasCropper) {
        const {error: dimensionsError} = await checkImageDimensions(file, {
          width: restrictions.width,
          height: restrictions.height,
        });

        if (dimensionsError) {
          return Promise.reject(dimensionsError);
        }

        resultFile = await handleCropFile(file, restrictions);
      }

      if (!resultFile) {
        return Promise.reject();
      }

      const sizeError = checkImageSize(resultFile, restrictions);

      if (sizeError) {
        return Promise.reject(sizeError);
      }

      const {dimensions, error: dimensionsError} = await checkImageDimensions(
        resultFile,
        restrictions,
      );

      if (dimensionsError) {
        return Promise.reject(dimensionsError);
      }

      if (customValidator) {
        resultFile = customValidator(resultFile, dimensions as TImageDimensions);
      }

      if (!resultFile) {
        return Promise.reject();
      }

      return Promise.resolve({file: resultFile, dimensions});
    }),
  );

  const successUploads = resolvedPromises
    .filter((promise) => promise.status === 'fulfilled')
    .map((promise) => (promise as PromiseFulfilledResult<TImagePickerResult>).value);

  const errorUploads = resolvedPromises
    .filter((promise) => promise.status === 'rejected' && (promise as PromiseRejectedResult).reason)
    .map((promise) => (promise as PromiseRejectedResult).reason as EImagePickerError);

  return {successUploads, errorUploads};
};

const getFormatsHint = (restrictions: TRestrictions) => {
  return [
    ...new Set(
      restrictions.accept
        ?.replaceAll(/ +/g, ' ')
        .replaceAll(/[,.]|(image\/)/g, '')
        .toUpperCase()
        .split(' ') ?? [],
    ),
  ].join(', ');
};

const showErrors = (
  errorUploads: EImagePickerError[],
  restrictions: TRestrictions,
  t: TFunction,
) => {
  const size = formatImageSize(restrictions.sizeInBytes);
  const formats = getFormatsHint(restrictions);

  const error = errorUploads
    .map((errorUpload) =>
      t(`EdnaImagePicker:errors.${errorUpload}`, {
        width: restrictions.width,
        height: restrictions.height,
        size,
        formats,
      }),
    )
    .join(', ');

  showNewNotification({
    type: 'error',
    message: t('EdnaImagePicker:imageError', {error}),
  });
};

export {
  requestFile,
  formatImageSize,
  getImageInfo,
  handleUpload,
  showErrors,
  isValidUrl,
  getFormatsHint,
};
